import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Edit, DollarSign, TrendingUp, CheckCircle, XCircle, Calendar } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import useCurrencyStore from '@/stores/currencyStore'
import { DetailHeader } from '@/components/ui/DetailHeader'
import { DetailSection } from '@/components/ui/DetailSection'

const CurrencyDetails = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const {
    currentCurrency,
    loading,
    error,
    fetchCurrencyById,
    clearError,
    clearCurrentCurrency
  } = useCurrencyStore()

  useEffect(() => {
    if (id) {
      fetchCurrencyById(id)
    }

    return () => {
      clearCurrentCurrency()
    }
  }, [id])

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusBadge = (enabled: boolean) => {
    return enabled ? (
      <Badge variant="default" className="flex items-center gap-1">
        <CheckCircle className="h-3 w-3" />
        Actif
      </Badge>
    ) : (
      <Badge variant="destructive" className="flex items-center gap-1">
        <XCircle className="h-3 w-3" />
        Inactif
      </Badge>
    )
  }

  const getSymbolPositionText = (position: 'before' | 'after') => {
    return position === 'before' ? 'Avant le montant' : 'Après le montant'
  }

  if (loading && !currentCurrency) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    )
  }

  if (!currentCurrency && !loading) {
    return (
      <div className="space-y-6">
        <DetailHeader
          title="Devise non trouvée"
          subtitle="La devise que vous recherchez n'existe pas ou a été supprimée."
          breadcrumbs={[
            { label: 'Devises', href: '/currencies' },
            { label: 'Détails', href: '#' }
          ]}
          backUrl="/currencies"
        />
        <Alert>
          <AlertDescription>
            Cette devise n'a pas pu être chargée. Elle a peut-être été supprimée ou vous n'avez pas les permissions nécessaires.
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  if (!currentCurrency) {
    return null
  }

  const breadcrumbs = [
    { label: 'Devises', href: '/currencies' },
    { label: currentCurrency.name, href: '#' }
  ]

  const actions = [
    {
      label: 'Modifier',
      icon: <Edit className="h-4 w-4" />,
      onClick: () => navigate(`/currencies/${id}/edit`),
      variant: 'default' as const
    }
  ]

  return (
    <div className="space-y-6">
      <DetailHeader
        title={currentCurrency.name}
        subtitle={`Informations détaillées sur la devise ${currentCurrency.name}`}
        breadcrumbs={breadcrumbs}
        backUrl="/currencies"
        actions={actions}
      />

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>
            {error}
            <Button variant="outline" size="sm" className="ml-4" onClick={clearError}>
              Fermer
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Informations principales */}
        <div className="lg:col-span-2 space-y-6">
          <DetailSection title="Informations générales" subtitle="Détails de base et configuration de la devise">
            <div className="flex items-center space-x-4 mb-6">
              <div className="p-3 bg-primary/10 rounded-full">
                <DollarSign className="h-8 w-8 text-primary" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">{currentCurrency.name}</h2>
                <p className="text-muted-foreground">
                  Code: {currentCurrency.code} • Symbole: {currentCurrency.symbol}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Statut</h3>
                  <div className="mt-1">
                    {getStatusBadge(currentCurrency.enabled)}
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Position du symbole</h3>
                  <p className="mt-1 text-lg font-medium">
                    {getSymbolPositionText(currentCurrency.symbol_position)}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Exemple: {currentCurrency.symbol_position === 'before' 
                      ? `${currentCurrency.symbol}100.00`
                      : `100.00 ${currentCurrency.symbol}`
                    }
                  </p>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Formatage des nombres</h3>
                  <div className="mt-1 space-y-1">
                    <p className="text-sm">
                      Séparateur décimal: <span className="font-medium">{currentCurrency.decimal_separator}</span>
                    </p>
                    <p className="text-sm">
                      Séparateur de milliers: <span className="font-medium">{currentCurrency.thousand_separator}</span>
                    </p>
                    <p className="text-sm">
                      Exemple: 1{currentCurrency.thousand_separator}234{currentCurrency.decimal_separator}56
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Précision</h3>
                  <div className="mt-1 flex items-center space-x-2">
                    <TrendingUp className="h-5 w-5 text-muted-foreground" />
                    <span className="text-lg font-medium">
                      {currentCurrency.decimal_digits} décimales
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Nombre de chiffres après la virgule
                  </p>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Arrondi</h3>
                  <p className="mt-1 text-lg font-medium">
                    {currentCurrency.rounding === 0 ? 'Aucun' : currentCurrency.rounding}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Valeur d'arrondi appliquée aux calculs
                  </p>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Exemple complet</h3>
                  <div className="mt-2 p-3 bg-muted rounded-lg">
                    <p className="text-2xl font-bold text-center">
                      {currentCurrency.symbol_position === 'before'
                        ? `${currentCurrency.symbol}1${currentCurrency.thousand_separator}234${currentCurrency.decimal_separator}56`
                        : `1${currentCurrency.thousand_separator}234${currentCurrency.decimal_separator}56 ${currentCurrency.symbol}`
                      }
                    </p>
                    <p className="text-sm text-muted-foreground text-center mt-1">
                      Format complet avec séparateurs
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </DetailSection>
        </div>

        {/* Métadonnées et actions */}
        <div className="space-y-6">
          <DetailSection title="Métadonnées" subtitle="Informations techniques et dates">
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-muted-foreground">Identifiant</h3>
                <p className="mt-1 font-mono text-sm bg-muted p-2 rounded">
                  {currentCurrency.id}
                </p>
              </div>

              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground">Créé le</h3>
                    <p className="mt-1">
                      {formatDate(currentCurrency.created_at)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground">Dernière modification</h3>
                    <p className="mt-1">
                      {formatDate(currentCurrency.updated_at)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </DetailSection>

          <DetailSection title="Actions rapides">
            <div className="space-y-2">
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => navigate(`/currencies/${id}/edit`)}
              >
                <Edit className="mr-2 h-4 w-4" />
                Modifier cette devise
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => navigate('/currencies')}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Retour à la liste
              </Button>
            </div>
          </DetailSection>

          <DetailSection title="Statistiques">
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm">Utilisation dans le système</span>
                <Badge variant="outline">À implémenter</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">Pays utilisant cette devise</span>
                <Badge variant="outline">À implémenter</Badge>
              </div>
            </div>
          </DetailSection>
        </div>
      </div>
    </div>
  )
}

export default CurrencyDetails
